import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import { AbstractControl, ControlContainer, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import Fuse from "fuse.js";
import { isEmpty } from "lodash";
import { Observable, Subscription } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { FormDataOptions } from "src/app/models/models";
import { SharedService } from "../../services/shared.service";

@Component({
	selector: "app-cbta-autocomplete",
	templateUrl: "./cbta-autocomplete.component.html",
	styleUrls: ["./cbta-autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CbtaAutocompleteComponent implements OnInit, OnDestroy {
	@Input() label: string = "";
	@Input() data: any;
	@Input() placeholder: string = "";
	@Input() options: FormDataOptions[] = [];
	@Input() field!: AbstractControl;
	@Input() readonly: boolean = false; // changes now trigger setDisabled()
	errorMessages!: { [key: string]: string };
	@Input() clearable: boolean = false;

	@Input() autoCompleteInput = new UntypedFormControl("");

	fromTypeOptions!: Observable<FormDataOptions[]>;
	value: any;
	@Output() focusOut: EventEmitter<any> = new EventEmitter<any>();
	@Output() selected: EventEmitter<any> = new EventEmitter<any>();
	isDisabled: boolean = false;

	@Input("isDisabled")
	set _(value: any) {
		this.isDisabled = value;
		this.setDisabled();
	}

	@ViewChild("inputField", { read: MatAutocompleteTrigger }) inputField!: MatAutocompleteTrigger;
	@ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
	@Input() panelWidth: any = "";
	@Input() useFuzzySearch = false;
	/** When true, only options from the list can be chosen (no free typing). */
	@Input() selectOnly = false;

	private fieldStatusSub?: Subscription;
	private fieldValueSub?: Subscription;
	private fuseInstance: Fuse<FormDataOptions> | null = null;

	constructor(
		public sharedService: SharedService,
		private cdr: ChangeDetectorRef,
		private controlContainer: ControlContainer,
	) {}

	ngOnInit(): void {
		this.fromTypeOptions = this.autoCompleteInput.valueChanges.pipe(
			startWith(""),
			map((value) => {
				// do not set errors while disabled
				if (!this.autoCompleteInput.disabled && isEmpty(value)) {
					this.autoCompleteInput.setErrors({ required: true });
				}
				return this._filter(value ?? "");
			}),
		);

		// Initial sync & subscribe to enabled/disabled transitions on parent control
		this.syncToField();
		this.setDisabled();

		if (!this.autoCompleteInput.disabled && isEmpty(this.autoCompleteInput.value)) {
			this.autoCompleteInput.setErrors({ required: true });
		}

		window.addEventListener("scroll", this.scrollEvent, true);
		this.updateFuseInstance();

		this.cdr.detectChanges();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["data"]) {
			this.syncDisplayFromField(this.data);
			this.selected.emit(this.data);
		}

		// React when parent passes a new control or flips readonly
		if (changes["field"] || changes["readonly"]) {
			this.syncToField();
			this.setDisabled();
		}

		if (changes["options"] || changes["useFuzzySearch"]) {
			this.updateFuseInstance();
		}
	}

	ngOnDestroy(): void {
		this.fieldStatusSub?.unsubscribe();
		this.fieldValueSub?.unsubscribe();
		window.removeEventListener("scroll", this.scrollEvent, true);
	}

	/** Keep internal control disabled/enabled in lockstep with parent control */
	private setDisabled() {
		const shouldDisable = !!(this.isDisabled || this.readonly || this.field?.disabled);

		if (shouldDisable) {
			if (!this.autoCompleteInput.disabled) {
				this.autoCompleteInput.disable({ emitEvent: false });
				this.autoCompleteInput.setErrors(null); // clear local errors while disabled
			}
		} else {
			if (this.autoCompleteInput.disabled) {
				this.autoCompleteInput.enable({ emitEvent: false });
			}
			// don’t force errors here; let validators run naturally
		}

		this.cdr.markForCheck();
	}

	/** Subscribe to status changes of the parent FormControl */
	private syncToField() {
		this.fieldStatusSub?.unsubscribe();
		this.fieldValueSub?.unsubscribe();
		if (this.field) {
			this.fieldStatusSub = this.field.statusChanges.subscribe(() => {
				this.setDisabled();
			});
			this.fieldValueSub = this.field.valueChanges.subscribe((value) => {
				this.syncDisplayFromField(value);
			});
			this.syncDisplayFromField(this.field.value);
		}
	}

	/** Keep visible input aligned when the bound form control is patched (e.g. draft load). */
	private syncDisplayFromField(value: unknown): void {
		if (value == null || value === "") {
			if (this.autoCompleteInput.value !== "" && this.autoCompleteInput.value != null) {
				this.autoCompleteInput.setValue("", { emitEvent: false });
				this.cdr.markForCheck();
			}
			return;
		}
		const current = this.autoCompleteInput.value;
		const sameObject =
			typeof value === "object" &&
			typeof current === "object" &&
			value != null &&
			current != null &&
			(value as FormDataOptions).code === (current as FormDataOptions).code;
		const sameString = typeof value === "string" && value === current;
		if (sameObject || sameString) return;

		this.autoCompleteInput.setValue(value, { emitEvent: false });
		this.cdr.markForCheck();
	}

	onInputKeydown(e: KeyboardEvent): void {
		if (e.key === " ") e.stopPropagation();
		if (this.selectOnly && !this.isNavigationKey(e)) {
			e.preventDefault();
		}
	}

	onInputPaste(e: ClipboardEvent): void {
		if (this.selectOnly) {
			e.preventDefault();
		}
	}

	onInputClick(): void {
		if (this.selectOnly && !this.autoCompleteInput.disabled) {
			this.autocompleteTrigger?.openPanel();
		}
	}

	private isNavigationKey(e: KeyboardEvent): boolean {
		return (
			e.key === "ArrowDown" ||
			e.key === "ArrowUp" ||
			e.key === "Enter" ||
			e.key === "Escape" ||
			e.key === "Tab" ||
			e.key === "Home" ||
			e.key === "End" ||
			e.key === "PageUp" ||
			e.key === "PageDown"
		);
	}

	private resolveOption(value: unknown): FormDataOptions | null {
		if (value == null || value === "") return null;
		if (typeof value === "object" && (value as FormDataOptions).code) {
			const code = (value as FormDataOptions).code;
			return (this.options ?? []).find((o) => o.code === code) ?? null;
		}
		const query = String(value).trim().toLowerCase();
		if (!query) return null;
		return (
			(this.options ?? []).find(
				(o) => (o.description ?? "").toLowerCase() === query || (o.code ?? "").toLowerCase() === query,
			) ?? null
		);
	}

	onSelectionChange(event: MatAutocompleteSelectedEvent) {
		if (!isEmpty(event.option.value)) {
			this.focusOut.emit(event.option.value);
		}
	}

	autocompleteFocusOut() {
		this.field?.markAsTouched({ onlySelf: true });
		this.autoCompleteInput.markAsTouched({ onlySelf: true });
		if (this.selectOnly) {
			const resolved = this.resolveOption(this.autoCompleteInput.value);
			if (resolved) {
				this.autoCompleteInput.setValue(resolved, { emitEvent: false });
				this.focusOut.emit(resolved);
				return;
			}
			this.syncDisplayFromField(this.field?.value);
			if (isEmpty(this.field?.value)) {
				this.syncEmptyRequiredError();
			}
			return;
		}
		if (!isEmpty(this.autoCompleteInput.value)) {
			this.focusOut.emit(this.autoCompleteInput.value);
		} else {
			this.syncEmptyRequiredError();
		}
	}

	/** Mirror `required` only when this control's own validator demands a value. */
	private syncEmptyRequiredError(): void {
		if (!this.field) {
			this.autoCompleteInput.setErrors(null);
			return;
		}
		this.field.updateValueAndValidity({ emitEvent: false });
		if (this.field.hasError("required")) {
			this.autoCompleteInput.setErrors({ required: true });
			return;
		}
		this.autoCompleteInput.setErrors(null);
	}

	displayFn(option: FormDataOptions | string): string {
		if (option == null) return "";
		if (typeof option === "string") return option;
		return (option as FormDataOptions).description ?? "";
	}

	private updateFuseInstance(): void {
		if (this.useFuzzySearch && Array.isArray(this.options) && this.options.length > 0) {
			this.fuseInstance = new Fuse(this.options, {
				keys: ["description", "code"],
				threshold: 0.4,
			});
		} else {
			this.fuseInstance = null;
		}
	}

	private _filter(value: FormDataOptions | string): FormDataOptions[] {
		const query = (typeof value === "string" ? value : ((value && (value as FormDataOptions).description) ?? ""))
			.toString()
			.trim()
			.toLowerCase();
		if (!query) return this.options ?? [];

		if (this.useFuzzySearch && this.fuseInstance) {
			const results = this.fuseInstance.search(query);
			return results.map((r) => r.item);
		}
		const desc = (o: FormDataOptions) => (o?.description ?? "").toLowerCase();
		return (this.options ?? []).filter((option) => desc(option).includes(query));
	}

	get parentForm() {
		return this.controlContainer.control as UntypedFormGroup;
	}

	get isFieldHasError() {
		// avoid generating errors while disabled
		this.autoCompleteInput.setErrors(null);
		if (this.field && this.field.touched && this.field.invalid) {
			this.autoCompleteInput.markAsTouched({ onlySelf: true });
		}
		const showFieldError = this.field && (this.field.dirty || this.field.touched);
		if (showFieldError && this.field?.hasError("required")) {
			this.autoCompleteInput.setErrors({ required: true });
			return true;
		} else if (showFieldError && this.field?.hasError("duplicateManevuer")) {
			this.autoCompleteInput.setErrors({ duplicateManevuer: true });
			return true;
		}
		return false;
	}

	hasError(errorCode: string): boolean {
		const control = this.field;
		return control.hasError(errorCode) && (control.dirty || control.touched);
	}

	scrollEvent = (_event: any): void => {
		if (this.inputField?.panelOpen) this.inputField.updatePosition();
	};

	clearInupt() {
		this.autoCompleteInput.patchValue("");
		this.field.patchValue("");
		this.field?.markAsTouched({ onlySelf: true });
		this.autoCompleteInput.markAsTouched({ onlySelf: true });
		this.syncEmptyRequiredError();
		this.autocompleteTrigger.openPanel();
	}
}
